import * as React from 'react'
import { Button, Checkbox, FormControlLabel, Typography, useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'
import Link from '../src/Link'
import Logo from '@svg/Logo'
import FdSymbol from '@svg/FdSymbol'
import SquareIcon from '@svg/SquareIcon'
import UserIcon from '@svg/UserIcon'
import LockKeyIcon from '@svg/LockKeyIcon'
import { ConsentFormData, LoginFormData } from '@common/interfaces'
import { submitLogin, updateDefaultLanguage } from '@/services/fd-api.service'
import { useRouter, useSearchParams } from 'next/navigation'
import { setUserObjData } from '@common/services/cookies.service'
import { useUserContext } from '@/contexts/UserContext'
import WarningIcon from '@svg/WarningIcon'
import { useToastsContext } from '@/contexts/ToastsContext'
import { notificationType as Type } from '@/Components/FdComponents/ToastMessages'
// import LanguageList from '@/Components/FdComponents/LanguageList'
import { useLanguageContext } from '@/contexts/LanguageContext'

export default function Home() {
  const router = useRouter()
  const queryParams = useSearchParams()
  const redirectTo = queryParams.get('redirectTo')

  const [errors, setErrors] = React.useState<any>({} as any)
  const [formData, setFormData] = React.useState<LoginFormData>({
    username: '',
    password: '',
    tnc: false
  } as LoginFormData)

  const [isLoading, setIsLoading] = React.useState(true)
  const { showToast } = useToastsContext()
  const { language, languageList, translations, loadTranslations } = useLanguageContext()
  const { refreshUser, clearUser, isLoggedIn, loading } = useUserContext()

  const leftSide = 360

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC')
      const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey

      if (
        e.key === 'F12' ||
        (ctrlOrCmd && e.shiftKey && ['I', 'J', 'C'].includes(e.key.toUpperCase())) ||
        (ctrlOrCmd && e.key.toUpperCase() === 'U')
      ) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  React.useEffect(() => {
    if (loading) return
    if (isLoggedIn) {
      const path = redirectTo ? redirectTo : '/Home'
      router.replace(path)
    } else {
      clearUser()
    }
    setIsLoading(false)
  }, [isLoggedIn, loading])

  React.useEffect(() => {
    loadTranslations('login')
    setErrors({})
  }, [language])

  const onCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const errs: any = { ...errors }
    errs.isError = false
    errs['api_msg'] = ''

    if (!formData.username) {
      errs['username'] = `${translations.USER_ID} ${translations.REQUIRED_FIELD}`
      errs.isError = true
    }

    if (!formData.password) {
      errs['password'] = `${translations.PASSWORD} ${translations.REQUIRED_FIELD}`
      errs.isError = true
    }

    if (!e.target.checked) {
      errs[e.target.name] = `${translations.TNC_ERROR}`
      errs.isError = true
    } else {
      errs[e.target.name] = ''
    }

    setErrors({ ...errs })
    setFormData({
      ...formData,
      [e.target.name]: e.target.checked
    })
  }

  const handleSubmit = (event: any) => {
    event.preventDefault()
    setIsLoading(true)
    const errs: any = { ...errors }
    const cFormData: ConsentFormData = { privacy: true, terms: true }

    if (!formData.username || !formData.password || !formData.tnc) {
      if (!formData.username) {
        errs['username'] = `${translations.USER_ID} ${translations.REQUIRED_FIELD}`
        errs.isError = true
      }

      if (!formData.password) {
        errs['password'] = `${translations.PASSWORD} ${translations.REQUIRED_FIELD}`
        errs.isError = true
      }

      if (!formData.tnc) {
        errs['tnc'] = `${translations.TNC_ERROR}`
        errs.isError = true
      }

      setErrors({ ...errs })
      setIsLoading(false)
      return
    }

    submitLogin(formData)
      .then(async (response: any) => {
        if (response.success) {
          const { languageId } = languageList.find((item) => item.languageCode === language) || {}
          setUserObjData(cFormData, 'tnc')
          await refreshUser()
          showToast({
            toastMessage: `${translations.WELCOME_BACK}`,
            toastMessageType: Type.Success
          })
          if (languageId) {
            const path = redirectTo ? redirectTo : '/Home'
            updateDefaultLanguage(languageId)
              .then(() => {
                router.replace(path)
              })
              .catch(() => {
                router.replace(path)
              })
          }
        } else {
          showToast({
            toastMessage: response.message,
            toastMessageType: Type.Error
          })
        }
      })
      .catch((err) => {
        console.log(err)
        setIsLoading(false)
        errs.isError = !!err.message
        errs['api_msg'] = err.message || `${translations.API_DEFAULT_ERROR}`
        showToast({
          toastMessage: errs['api_msg'],
          toastMessageType: Type.Error
        })
        setErrors({ ...errs })
      })
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const errs: any = { ...errors }
    errs.isError = false
    errs['api_msg'] = ''
    const mapper: any = {
      username: `${translations.USER_ID}`,
      password: `${translations.PASSWORD}`
    }
    if (!e.target.value) {
      errs[e.target.name] = `${mapper[e.target.name]} ${translations.REQUIRED_FIELD}`
      errs.isError = true
    } else {
      errs[e.target.name] = ''
    }
    setErrors({ ...errs })
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const theme = useTheme()
  return (
    <Box sx={{ display: 'flex' }} onContextMenu={(e) => e.preventDefault()}>
      <Box
        sx={{
          width: { xs: '100%', sm: '100%', md: `${leftSide}px` },
          background: (theme) => theme.palette.primary.main,
          position: 'fixed',
          left: '0',
          bottom: '0',
          top: '0'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            '& svg': { width: '15.31rem', height: '2.625rem' }
          }}
        >
          <Logo fdTextColor={theme.palette.primary.contrastText} symbolColor={theme.palette.primary.contrastText} />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            right: '-5rem',
            top: '-5rem',
            zIndex: '-1',
            color: 'rgba(2555,255,255,0.2)',
            opacity: '0.2'
          }}
        >
          <FdSymbol height='13rem' width='13rem' />
        </Box>
        <Box
          sx={{
            position: 'absolute',
            right: '0',
            left: '0',
            bottom: '0',
            color: '#fff',
            textAlign: 'center'
          }}
        >
          <SquareIcon />
        </Box>
      </Box>
      <Box
        sx={{
          width: { md: `calc(100% - ${leftSide}px)` },
          left: { md: `${leftSide}px` },
          position: 'relative'
        }}
      >
        <Box
          sx={{
            height: '10vh',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
            marginRight: 2
          }}
        >
          {/* <LanguageList /> */}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '85vh',
            m: { xs: 'auto', md: 'initial' }
          }}
        >
          <Box>
            <Card sx={{ width: '21rem', mx: 'auto', p: 3 }}>
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start' sx={{ '& svg': { color: theme.palette.primary.main } }}>
                      <UserIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end' sx={{ '& svg': { color: theme.palette.error.main } }}>
                      {errors?.username ? <WarningIcon /> : ''}{' '}
                    </InputAdornment>
                  )
                }}
                fullWidth
                required
                id='username'
                label={translations.USER_ID}
                name='username'
                autoComplete='username'
                placeholder={translations.USER_NAME}
                autoFocus
                value={formData.username}
                onChange={onChange}
                error={!!errors?.username}
                helperText={errors?.username}
              />
              <TextField
                sx={{ mt: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start' sx={{ '& svg': { color: theme.palette.primary.main } }}>
                      <LockKeyIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end' sx={{ '& svg': { color: theme.palette.error.main } }}>
                      {errors?.password ? <WarningIcon /> : ''}{' '}
                    </InputAdornment>
                  )
                }}
                fullWidth
                required
                name='password'
                label={translations.PASSWORD}
                type='password'
                id='password'
                placeholder={translations.PASSWORD}
                value={formData.password}
                autoComplete='current-password'
                onChange={onChange}
                error={!!errors?.password}
                helperText={errors?.password}
              />
              <Box sx={{ textAlign: 'right', mt: 1 }}>
                <Typography variant='caption' sx={{ color: theme.palette.text.primary }}>
                  <Link href='/Password/Forgot' sx={{ textDecoration: 'none' }}>
                    {translations.FORGOT_PASSWORD}?
                  </Link>
                </Typography>
              </Box>
              <Box>
                <FormControlLabel
                  sx={{ '&.MuiFormControlLabel-root': { alignItems: 'start' }, mt: 2 }}
                  control={<Checkbox color='success' sx={{ ml: -1 }} checked={formData.tnc} name={'tnc'} onChange={onCheckboxChange} />}
                  label={
                    <>
                      <Typography variant='caption' sx={{ color: theme.palette.text.primary }}>
                        {translations.TNC_LABEL}&nbsp;
                        <Link
                          href={translations.TNC_LINK || 'https://www.fielddrive.com/terms-and-conditions'}
                          target='_blank'
                          sx={{ textDecoration: 'none' }}
                        >
                          {translations.TNC}
                        </Link>
                        &nbsp;{translations.LOWER_AND}&nbsp;
                        <Link
                          href={translations.POLICY_LINK || 'https://www.fielddrive.com/privacy-policy'}
                          target='_blank'
                          sx={{ textDecoration: 'none' }}
                        >
                          {translations.POLICY}
                        </Link>
                        . {translations.POLICY_LABEL}
                      </Typography>
                    </>
                  }
                />
              </Box>

              <Button disabled={errors.isError || isLoading} onClick={handleSubmit} variant='contained' sx={{ mt: 2 }} fullWidth>
                {translations.LOGIN}
              </Button>
              {errors.api_msg && (
                <Typography component='p' variant='caption' sx={{ color: 'error.main', textAlign: 'center', mt: 2 }}>
                  {errors.api_msg}
                </Typography>
              )}
              {errors?.tnc && (
                <Typography component='p' variant='caption' sx={{ color: 'error.main', textAlign: 'center', mt: 2 }}>
                  {errors.tnc}
                </Typography>
              )}
            </Card>
            <Typography variant='caption' sx={{ textAlign: 'center', mt: 2, display: 'block', color: 'text.secondary' }}>
              {translations.SUPPORT_LABEL} <br />
              <Link href='mailto:support@fielddrive.com' sx={{ textDecoration: 'none' }}>
                support@fielddrive.com
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
